import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks';
import { fetchIDEStats, fetchWebStats, fetchUserStats, fetchOrgStats } from '../store/slices/dashboardSlice';
import StatsCard from '../components/dashboard/StatsCard';
import TokenUsageChart from '../components/dashboard/TokenUsageChart';
import TotalCredits from '../components/dashboard/TotalCredits';
import AccessDistribution from '../components/dashboard/AccessDistribution';
import SystemHealth from '../components/dashboard/SystemHealth';
import IDEStats from '../components/dashboard/IDEStats';
import WebStats from '../components/dashboard/WebStats';
import DashboardFilters from '../components/dashboard/DashboardFilters';
import ScrollHeader from '../components/common/ScrollHeader';
import WidgetSkeleton from '../components/common/WidgetSkeleton';
import MemberDistribution from '../components/dashboard/MemberDistribution';
import UserRegistrationChart from '../components/dashboard/UserRegistrationChart';
import CreditUsageWidget from '../components/limits/CreditUsageWidget';
import CreditPoolWidget from '../components/limits/CreditPoolWidget';
import FeatureGate from '../components/limits/FeatureGate';
import {
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const dashboard = useAppSelector((state) => state.dashboard);
  const {
    ideStats,
    webStats,
    userStats,
    orgStats,
    isLoading,
    isIdeStatsLoading,
    isWebStatsLoading,
  } = dashboard;

  // Filter states
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [orgId, setOrgId] = useState('');

  // Load basic stats immediately
  const loadBasicStats = () => {
    dispatch(fetchUserStats(30));
    dispatch(fetchOrgStats());
  };

  useEffect(() => {
    loadBasicStats();
  }, [dispatch]);

  // Load detailed stats with filters
  const loadDetailedStats = () => {
    const params = {
      start_date: startDate,
      end_date: endDate,
      ...(orgId && { org_id: orgId })
    };

    dispatch(fetchIDEStats(params));
    dispatch(fetchWebStats(params));
  };

  useEffect(() => {
    // Add a small delay to ensure basic stats load first
    const timer = setTimeout(loadDetailedStats, 500);
    return () => clearTimeout(timer);
  }, [dispatch, startDate, endDate, orgId]);

  // Error display component
  const ErrorMessage = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
      <p className="text-red-500 mb-2">{message}</p>
      <button
        onClick={onRetry}
        className="text-sm text-red-500 hover:text-red-600 font-medium underline"
      >
        Retry
      </button>
    </div>
  );

  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Calculate active users from IDE and Web stats (users with credit usage)
  const calculateActiveUsers = () => {
    const activeUserIds = new Set<string | number>();
    
    // Add users from IDE stats who have credit usage > 0
    if (ideStats?.ide_stats?.user_costs) {
      ideStats.ide_stats.user_costs.forEach(user => {
        if (user.total_cost > 0) {
          activeUserIds.add(user.user_id);
        }
      });
    }
    
    // Add users from Web stats who have credit usage > 0
    if (webStats?.web_stats?.user_costs) {
      webStats.web_stats.user_costs.forEach(user => {
        const credits = user.input_tokens || 0 + user.output_tokens || 0;
        if (credits > 0) {
          activeUserIds.add(user.user_id);
        }
      });
    }
    
    return activeUserIds.size;
  };

  const activeUsersCount = calculateActiveUsers();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <ScrollHeader title="Dashboard" />
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Monitor your system's performance and usage metrics
        </p>
      </div>

      {/* Filters */}
      <DashboardFilters
        startDate={startDate}
        endDate={endDate}
        orgId={orgId}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onOrgIdChange={setOrgId}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          Array(isSuperAdmin ? 4 : 2).fill(0).map((_, i) => (
            <WidgetSkeleton key={i} />
          ))
        ) : dashboard.error ? (
          <div className="col-span-full">
            <ErrorMessage 
              message="Failed to load basic statistics" 
              onRetry={loadBasicStats} 
            />
          </div>
        ) : (
          <>
            {userStats && (
              <>
                <StatsCard
                  title="Total Users"
                  value={userStats.status_distribution.active + userStats.status_distribution.disabled}
                  changeText={`+${userStats.daily_registrations.reduce((acc, curr) => acc + curr.count, 0)} in 30 days`}
                  icon={<UsersIcon />}
                />
                <StatsCard
                  title="Active Users"
                  value={activeUsersCount}
                  changeText={``}
                  icon={<UserGroupIcon />}
                />
                {isSuperAdmin && orgStats && (
                  <>
                    <StatsCard
                      title="Total Organizations"
                      value={Object.keys(orgStats.member_distribution).length}
                      icon={<BuildingOfficeIcon />}
                    />
                    <StatsCard
                      title="Active Organizations"
                      value={orgStats.organization_stats.active}
                      change={Math.round((orgStats.organization_stats.active / Object.keys(orgStats.member_distribution).length) * 100)}
                      icon={<ChartBarIcon />}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Total Credits */}
      <TotalCredits 
        ideStats={ideStats}
        webStats={webStats}
        isLoading={isIdeStatsLoading || isWebStatsLoading}
      />

      {/* IDE & Web Stats */}
      <div className="grid grid-cols-1 gap-6">
        {dashboard.ideError ? (
          <div className="col-span-full">
            <ErrorMessage 
              message="Failed to load IDE statistics" 
              onRetry={loadDetailedStats} 
            />
          </div>
        ) : (
          <IDEStats stats={ideStats} isLoading={isIdeStatsLoading} />
        )}
        {dashboard.webError ? (
          <div className="col-span-full">
            <ErrorMessage 
              message="Failed to load Web statistics" 
              onRetry={loadDetailedStats} 
            />
          </div>
        ) : (
          <WebStats stats={webStats} isLoading={isWebStatsLoading} />
        )}
      </div>

      {/* Limits & Usage Widgets */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Credit Usage Widget - Available to all users */}
        <CreditUsageWidget 
          title="My Credit Usage"
          showTrend={true}
          onLimitExceeded={() => {
            // Could trigger a toast notification or modal
            console.log('Credit limit exceeded!');
          }}
        />
        
        {/* Credit Pool Widget - Only for org admins and super admins */}
        {(currentUser?.role === 'org_admin' || currentUser?.role === 'super_admin') && (
          <CreditPoolWidget 
            title="Organization Credit Pool"
            showTopupButton={currentUser?.role === 'super_admin'}
            onPoolLow={() => {
              console.log('Credit pool is running low!');
            }}
          />
        )}
        
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {isLoading ? (
          <WidgetSkeleton />
        ) : userStats ? (
          <UserRegistrationChart registrations={userStats.daily_registrations} />
        ) : null}

        {isWebStatsLoading ? (
          <WidgetSkeleton />
        ) : webStats?.web_stats?.access_distribution ? (
          <AccessDistribution
            distribution={webStats.web_stats.access_distribution}
          />
        ) : null}
      </div>

      {/* Organization Distribution */}
      {isSuperAdmin && orgStats && !isLoading && (
        <div className="grid grid-cols-1 gap-6">
          <MemberDistribution distribution={orgStats.member_distribution} />
        </div>
      )}

      {/* System Health */}
      {isSuperAdmin && !isLoading && (
        <div className="grid grid-cols-1 gap-6">
          <SystemHealth health={{
            status: 'healthy',
            last_checked: new Date().toISOString(),
            database_connection: 'connected',
            api_status: 'operational'
          }} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
